import { gql } from "@apollo/client";
import _ from "lodash";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  ArrowRightLeftIcon,
  BuildingIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  DollarSignIcon,
  DownloadIcon,
  FileTextIcon,
  GitBranchIcon,
  HashIcon,
  ImageIcon,
  LandmarkIcon,
  LinkIcon,
  MailIcon,
  RotateCwIcon,
  SendIcon,
  TableIcon,
  TextAlignStartIcon,
  UserIcon,
  WalletIcon,
} from "lucide-react";
import { BudgetLinesOfRecord } from "@/objects/BudgetLineObject";
import { iconsByTypename } from "@/objects/@icons";

import {
  CurrencyEnum,
  DocumentEnum,
  DocumentFileProcessingStateEnum,
  DocumentFragment,
  DocumentPaymentsQuery,
  DocumentPaymentMethodEnum,
  DocumentPaymentScheduleStatusEnum,
  InvoiceStateEnum,
} from "@/__generated__/graphql";
import { TableColumnType } from "@/components/@types";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Icon } from "@/components/atoms/Icon";
import { PdfThumbnail } from "@/components/molecules/PdfPreview";
import { DocumentPreview } from "@/components/organisms/DocumentPreview";
import { FieldTable } from "@/components/organisms/Field";
import {
  useNodeField,
  useNodeLayoutStore,
} from "@/components/organisms/Node/Node.hooks";
import { reconcileServerStateWithPendingOperations } from "@/components/organisms/Node/Node.store";
import { Mutations, Queries } from "@/constants";
import { EnumColors, Values } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useEnumColor } from "@/hooks/useEnumColor";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { useShallow } from "zustand/react/shallow";

type DocumentLineItem = {
  id?: string | null;
  identifier?: string | null;
  metadata?: Record<string, unknown> | null;
  /** When the bank posted the line, on a statement. */
  effectiveDate?: string | null;
  amount?: number | null;
  amountSubtotal?: number | null;
  amountTax?: number | null;
  quantity?: number | null;
  /** Kept on the row only while its quantity is typed (``unitPriceOf``). */
  unitPrice?: number | null;
  description?: string | null;
  tax?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
    totalRate?: number | string | null;
    rates?: ({ rate?: number | string | null } | null)[] | null;
  } | null;
  chartedAccount?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
  } | null;
};

type DocumentPaymentScheduleItem = {
  id?: string | null;
  identifier?: string | null;
  metadata?: Record<string, unknown> | null;
  name?: string | null;
  percent?: number | string | null;
  amount?: number | string | null;
  dateDue?: string | null;
  status?: DocumentPaymentScheduleStatusEnum | null;
  datePaid?: string | null;
  dateClaimed?: string | null;
  claimedReference?: string | null;
  dateAcknowledged?: string | null;
};

type DocumentWithLineItems = DocumentFragment & {
  lineItems?: DocumentLineItem[] | null;
};

type DocumentWithPaymentSchedule = DocumentFragment & {
  paymentSchedule?: DocumentPaymentScheduleItem[] | null;
};

const PAYMENT_SCHEDULE_STATUS_LABELS: Record<
  DocumentPaymentScheduleStatusEnum,
  string
> = {
  [DocumentPaymentScheduleStatusEnum.Pending]: "Awaiting payment",
  [DocumentPaymentScheduleStatusEnum.AwaitingConfirmation]:
    "Reported paid — needs acknowledgement",
  [DocumentPaymentScheduleStatusEnum.Paid]: "Paid",
  [DocumentPaymentScheduleStatusEnum.Cancelled]: "Cancelled",
};

function useHydrateDocumentLineItems(documentId: string | undefined | null) {
  const store = useNodeLayoutStore();
  const { data, loading, refetch } = useBackendQuery(
    Queries.DOCUMENT_LINE_ITEMS_QUERY,
    {
      variables: { id: documentId! },
      skip: !documentId,
      fetchPolicy: "cache-first",
    },
  );

  const applyLineItems = useCallback(
    (node: unknown) => {
      const document = node as DocumentWithLineItems | null | undefined;
      if (!document || document.__typename !== "DocumentObject") {
        return;
      }
      const state = store.getState();
      if (state.interactionLockCount > 0) {
        return;
      }
      const lineItems = document.lineItems ?? [];
      const serverState = {
        ...(state.fieldValuesInitial as Record<string, unknown>),
        lineItems,
      };
      const reconciled = reconcileServerStateWithPendingOperations(
        serverState,
        state.pendingFieldOperations,
      );
      store.setState({
        fieldValuesInitial: {
          ...state.fieldValuesInitial,
          lineItems,
        },
        fieldValues: {
          ...state.fieldValues,
          lineItems: reconciled.fieldValues.lineItems,
        },
        pendingFieldOperations: reconciled.pendingFieldOperations,
      });
    },
    [store],
  );

  // First load only. After that the store is authoritative: this query's cache
  // still holds pre-save rows (a save returns the document without line items),
  // so re-reading it would undo the edit that was just saved.
  useLayoutEffect(() => {
    if (
      (store.getState().fieldValuesInitial as DocumentWithLineItems)
        ?.lineItems != null
    ) {
      return;
    }
    applyLineItems(data?.node);
  }, [applyLineItems, data, store]);

  // A save moves ``updatedAt``: that is the signal the server has a newer answer
  // than the cache. Apply what the refetch itself returns rather than the
  // ambient value, which can still be the cached one.
  const documentUpdatedAt = store(
    (state) => (state.fieldValues as { updatedAt?: string })?.updatedAt,
  );
  const lastUpdatedAtRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = lastUpdatedAtRef.current;
    lastUpdatedAtRef.current = documentUpdatedAt;
    if (!documentId || !data || !previous || previous === documentUpdatedAt) {
      return;
    }
    void refetch().then((result) => applyLineItems(result.data?.node));
  }, [applyLineItems, documentId, data, documentUpdatedAt, refetch]);

  return { loading };
}

/** How wide a card draws the file it stands for: taller than the card has room for, its page runs off the card's foot. */
const CARD_PREVIEW_WIDTH = 160;

/**
 * The file a document's card stands for, on its right from the top down, its
 * page running off the card's foot: an image as it
 * is, a PDF by its first page, a web page (a receipt that came as an email)
 * by the page itself, anything else by a file's mark - and, while it is still being read,
 * a spinner over it.
 */
function DocumentCardPreview({
  file,
  isReading,
}: {
  file: DocumentFragment["file"];
  isReading: boolean;
}) {
  const name = file?.name?.toLowerCase() ?? "";
  const isImage = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/.test(name);
  const isPdf = name.endsWith(".pdf");
  const isHtml = /\.html?$/.test(name);
  return (
    <div
      data-testid="document-card-preview"
      className="relative shrink-0 w-40 [aspect-ratio:8.5_/_11] rounded overflow-hidden [border-width:1px] [border-style:solid] border-border shadow-xs bg-muted flex items-center justify-center text-muted-foreground/80"
    >
      {file?.url && isImage ? (
        <img
          src={file.url}
          alt=""
          className="w-full h-full object-cover object-top"
        />
      ) : file?.url && isPdf ? (
        <div className="self-start">
          <PdfThumbnail url={file.url} width={CARD_PREVIEW_WIDTH} />
        </div>
      ) : file?.url && isHtml ? (
        <iframe
          src={file.url}
          title=""
          aria-hidden
          tabIndex={-1}
          // Markup a third party wrote, as the document's Preview shows it:
          // it runs no script and goes nowhere. A picture, not a page to use.
          sandbox=""
          referrerPolicy="no-referrer"
          className="absolute top-0 left-0 w-150 h-[776.4705882352941px] [border:0px_solid] transform-[scale(0.26666666666666666)] origin-[top_left] pointer-events-none bg-white"
        />
      ) : (
        <Icon ReactIcon={FileTextIcon} size="md" />
      )}
      {isReading ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              data-testid="document-card-reading"
              role="status"
              aria-label="Reading the file"
              className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,var(--card)_60%,transparent)]"
            >
              <Spinner />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">Reading the file</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

function DocumentCard({
  data,
  actions: _actions,
  isLoading = false,
}: ObjectCardProps<DocumentFragment>) {
  // Under its name (the card's title): what it comes to, who it is from - or,
  // a statement, the account it is of - and one line of what it is and where
  // it stands. Its number and what it is about are the list's and its
  // preview's to say.
  const paidColor = useEnumColor(EnumColors.GREEN).color;
  const overdueColor = useEnumColor(EnumColors.RED).color;
  const isStatement = data?.documentType === DocumentEnum.BankStatement;
  // In another currency than its business's: its sign says which, as its row
  // in the list does.
  const currency =
    data?.amountCurrency &&
    data?.entity?.currency &&
    data.amountCurrency !== data.entity.currency
      ? data.amountCurrency
      : null;
  const total = data?.amountTotal != null ? Number(data.amountTotal) : null;
  const due = data?.amountDue != null ? Number(data.amountDue) : null;
  // An invoice or a bill paid in part: what is left of it to pay, first.
  const isPartlyPaid =
    (data?.documentType === DocumentEnum.Invoice ||
      data?.documentType === DocumentEnum.Bill) &&
    total != null &&
    due != null &&
    due !== 0 &&
    Math.abs(due) < Math.abs(total);
  const dueDate = data?.dateDue ? new Date(data.dateDue) : null;
  const state = data?.invoiceState ?? null;
  // Where it stands: paid, put aside, late, due - else, a receipt or a
  // statement, the day it is of.
  const standing: { text: string; color?: string } | null =
    state === InvoiceStateEnum.Paid
      ? { text: "Paid", color: paidColor }
      : state === InvoiceStateEnum.Draft ||
          state === InvoiceStateEnum.Cancelled ||
          state === InvoiceStateEnum.Voided ||
          state === InvoiceStateEnum.Refunded ||
          state === InvoiceStateEnum.PartiallyRefunded
        ? { text: _.upperFirst(_.lowerCase(state)) }
        : dueDate && dueDate.getTime() < Date.now()
          ? {
              text: `Overdue since ${Utils.fDate(dueDate)}`,
              color: overdueColor,
            }
          : dueDate
            ? { text: `Due ${Utils.fDate(dueDate)}` }
            : data?.dateIssued
              ? {
                  text: `${isStatement ? "Ends " : ""}${Utils.fDate(new Date(data.dateIssued))}`,
                }
              : null;
  const kind = data?.documentType
    ? _.upperFirst(_.lowerCase(data.documentType))
    : null;
  const from = isStatement
    ? data?.financialAccount?.name
    : data?.contact?.organization?.name || data?.contact?.emailAddress;
  // Its file waiting to be read, or being read: what it says may yet change.
  const isReading =
    !isLoading &&
    (data?.fileProcessingState ===
      DocumentFileProcessingStateEnum.Unprocessed ||
      data?.fileProcessingState === DocumentFileProcessingStateEnum.Processing);
  return (
    // What it says on the left; on the right, the file it stands for - beside
    // the words rather than under them, its top level with theirs and its foot
    // cut by the card's edge, so the head of the page is what shows.
    <div className="flex gap-3 h-full min-h-0">
      <div className="flex flex-col gap-1.5 flex-1 min-w-0 min-h-0">
        {/* A statement's lines are what came in and went out, not a sum
            anyone owes: no amount of its own. */}
        {!isStatement && (isPartlyPaid || total != null) && (
          <div className="flex items-baseline gap-1.5 min-w-0">
            <Typography
              level="h4"
              data-testid="document-card-amount"
              className="[line-height:1.1] shrink-0"
            >
              <Skeleton loading={isLoading} variant="inline">
                {isPartlyPaid
                  ? `${Utils.fCurrency(due, false, false, currency)} due`
                  : Utils.fCurrency(total, false, false, currency)}
              </Skeleton>
            </Typography>
            {isPartlyPaid && (
              <Typography
                level="body-sm"
                data-testid="document-card-total"
                className="truncate text-muted-foreground"
              >
                <Skeleton loading={isLoading} variant="inline">
                  of {Utils.fCurrency(total, false, false, currency)}
                </Skeleton>
              </Typography>
            )}
          </div>
        )}

        {from && (
          <Typography
            level="body-sm"
            data-testid="document-card-from"
            className="truncate text-muted-foreground"
          >
            <Skeleton loading={isLoading} variant="inline">
              {from}
            </Skeleton>
          </Typography>
        )}

        {(kind || standing) && (
          <Typography
            level="body-xs"
            data-testid="document-card-standing"
            className="mt-auto truncate text-muted-foreground"
          >
            <Skeleton loading={isLoading} variant="inline">
              {kind}
              {kind && standing ? " · " : null}
              {standing ? (
                <span style={{ color: standing.color }}>{standing.text}</span>
              ) : null}
            </Skeleton>
          </Typography>
        )}
      </div>
      {data?.file?.url || isReading ? (
        <div className="self-start">
          {/* Standing in for one, the frame of a file without one: nothing is
              fetched for a record that is not there. */}
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <DocumentCardPreview
              file={isLoading ? null : data?.file}
              isReading={isReading}
            />
          </Skeleton>
        </div>
      ) : null}
    </div>
  );
}

function DocumentLineItemsTab({ size }: { size: "sm" | "md" }) {
  const store = useNodeLayoutStore();
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    "lineItems",
  );
  // ``useNodeField`` narrows ``fieldValues`` to the field's own value keys
  // (``lineItems`` and ``entity``), so the document id has to come from the
  // store. Reading it off the narrowed object left it undefined, which skipped
  // the query outright: the grid only ever had rows when the Preview tab had
  // already loaded them.
  // A document being written has only its form's placeholder id: no record
  // to read anything of it from.
  const documentId = store(
    useShallow((state) =>
      state.mode === "create"
        ? undefined
        : (state.fieldValues as DocumentFragment | undefined)?.id,
    ),
  );
  useHydrateDocumentLineItems(documentId);
  // The table waits until the lines are in the store, not until the query that
  // fetches them is done: they are put there afterwards, and in between the
  // table read as loaded with nothing in it — a flash of the empty table
  // between the skeletons and the lines. A document being written has no lines
  // to wait for; its table is its own from the start.
  const isWaitingForLines = store(
    (state) =>
      Boolean(documentId) &&
      (state.fieldValuesInitial as DocumentWithLineItems | undefined)
        ?.lineItems == null,
  );
  // A statement's lines are in the order the bank printed them: numbered, not
  // dragged into another.
  const isStatement = store(
    (state) =>
      (state.fieldValues as DocumentFragment | undefined)?.documentType ===
      DocumentEnum.BankStatement,
  );

  if (!fieldDefinition || fieldDefinition.type !== "table") {
    return null;
  }

  const value = (fieldDefinition.getValue(fieldValues) ??
    []) as DocumentLineItem[];
  const valueOptions = (fieldDefinition.getValueOptions?.(fieldValues) ?? {
    columns: [],
  }) as { columns: TableColumnType[] };
  // What its lines come to, under them - before tax, the tax, and the two
  // together: the total it prints. Each added as its column reads it. A
  // statement's lines are what the bank printed, not a bill to add up.
  const summaryRow = isStatement
    ? undefined
    : Object.fromEntries(
        valueOptions.columns.map((column) => {
          if (column.objectKey === "description") {
            return [column.objectKey, "Total"];
          }
          if (!["amount", "amountTax", "total"].includes(column.objectKey)) {
            // Nothing to add up: left empty, not read as a line would be.
            return [column.objectKey, <Fragment key={column.objectKey} />];
          }
          const sum = value.reduce(
            (total, line) => total + Number(column.getValue?.(line) ?? 0),
            0,
          );
          return [
            column.objectKey,
            <Typography
              key={column.objectKey}
              level={`body-${size}`}
              // As its column is: the total in bold, the rest not.
              className={cn(
                "w-full text-right",
                column.objectKey === "total" && "font-semibold",
              )}
            >
              {`${sum < 0 ? "-" : ""}${Utils.fCurrency(Math.abs(sum), false, true)}`}
            </Typography>,
          ];
        }),
      );

  return (
    <div className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden">
      <FieldTable
        value={value}
        valueOptions={valueOptions}
        events={fieldEvents}
        cellSize={size}
        isRowNumbered={isStatement}
        isLoading={isWaitingForLines}
        summaryRow={summaryRow}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

function useHydrateDocumentPaymentSchedule(
  documentId: string | undefined | null,
) {
  const store = useNodeLayoutStore();
  const { data, loading, refetch } = useBackendQuery(
    Queries.DOCUMENT_PAYMENT_SCHEDULE_QUERY,
    {
      variables: { id: documentId! },
      skip: !documentId,
      fetchPolicy: "cache-and-network",
    },
  );

  const applySchedule = useCallback(
    (node: unknown) => {
      const document = node as DocumentWithPaymentSchedule | null | undefined;
      if (!document || document.__typename !== "DocumentObject") {
        return;
      }
      const state = store.getState();
      if (state.interactionLockCount > 0) {
        return;
      }
      const paymentSchedule = document.paymentSchedule ?? [];
      const reconciled = reconcileServerStateWithPendingOperations(
        {
          ...(state.fieldValuesInitial as Record<string, unknown>),
          paymentSchedule,
        },
        state.pendingFieldOperations,
      );
      store.setState({
        fieldValuesInitial: {
          ...state.fieldValuesInitial,
          paymentSchedule,
        },
        fieldValues: {
          ...state.fieldValues,
          paymentSchedule: reconciled.fieldValues.paymentSchedule,
        },
        pendingFieldOperations: reconciled.pendingFieldOperations,
      });
    },
    [store],
  );

  useLayoutEffect(() => {
    if (
      (store.getState().fieldValuesInitial as DocumentWithPaymentSchedule)
        ?.paymentSchedule != null
    ) {
      return;
    }
    applySchedule(data?.node);
  }, [applySchedule, data, store]);

  const reload = useCallback(
    () => refetch().then((result) => applySchedule(result.data?.node)),
    [applySchedule, refetch],
  );

  // Instalment amounts are resolved server-side from their percentages, so a
  // save is the moment the grid's derived figures need replacing with the
  // authoritative ones. ``updatedAt`` moving is the signal that happened.
  const documentUpdatedAt = store(
    (state) => (state.fieldValues as { updatedAt?: string })?.updatedAt,
  );
  const lastUpdatedAtRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = lastUpdatedAtRef.current;
    lastUpdatedAtRef.current = documentUpdatedAt;
    if (!documentId || !data || !previous || previous === documentUpdatedAt) {
      return;
    }
    void reload();
  }, [documentId, data, documentUpdatedAt, reload]);

  return { loading, refetch: reload };
}

function DocumentPaymentScheduleTab({ size }: { size: "sm" | "md" }) {
  const store = useNodeLayoutStore();
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    "paymentSchedule",
  );
  // A document being written has only its form's placeholder id: no record
  // to read anything of it from.
  const documentId = store(
    useShallow((state) =>
      state.mode === "create"
        ? undefined
        : (state.fieldValues as DocumentFragment | undefined)?.id,
    ),
  );
  const { refetch } = useHydrateDocumentPaymentSchedule(documentId);
  // Waiting on the schedule itself, not on the query that fetches it: the
  // instalments are put in the store after it answers, and between the two the
  // table would read as an invoice paid in one go. A document being written
  // has no schedule to wait for.
  const isWaitingForSchedule = store(
    (state) =>
      Boolean(documentId) &&
      (state.fieldValuesInitial as DocumentWithPaymentSchedule | undefined)
        ?.paymentSchedule == null,
  );
  const { mutate: acknowledge, loading: isAcknowledging } = useBackendMutation({
    mutation: Mutations.ACKNOWLEDGE_PAYMENT_SCHEDULE_ITEM,
  });

  if (!fieldDefinition || fieldDefinition.type !== "table") {
    return null;
  }

  const value = (fieldDefinition.getValue(fieldValues) ??
    []) as DocumentPaymentScheduleItem[];
  const valueOptions = (fieldDefinition.getValueOptions?.(fieldValues) ?? {
    columns: [],
  }) as { columns: TableColumnType[] };

  // Instalments the payer says they have paid outside Stripe. Nothing is
  // booked until someone here confirms it, so they surface above the grid
  // rather than as another cell to notice.
  const awaitingAcknowledgement = value.filter(
    (item) =>
      item.status === DocumentPaymentScheduleStatusEnum.AwaitingConfirmation,
  );

  return (
    <div className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden">
      {awaitingAcknowledgement.length > 0 && (
        <div className="flex flex-col gap-2 p-3 [border-bottom-width:1px] [border-bottom-style:solid] border-border">
          {awaitingAcknowledgement.map((item) => (
            <div
              key={item.identifier}
              className="flex items-center gap-2 flex-wrap"
            >
              <Typography level="body-sm" className="flex-1 min-w-0">
                {item.name || "An instalment"} of{" "}
                {Utils.fCurrency(Number(item.amount ?? 0))} was reported as paid
                {item.claimedReference ? ` (${item.claimedReference})` : ""}
              </Typography>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  acknowledge({
                    variables: {
                      input: { scheduleItemIdentifier: item.identifier },
                    },
                    callbacks: { onSuccess: () => refetch() },
                  })
                }
                disabled={isAcknowledging}
              >
                {isAcknowledging && <Spinner />}Acknowledge payment
              </Button>
            </div>
          ))}
        </div>
      )}

      <FieldTable
        value={value}
        valueOptions={valueOptions}
        events={fieldEvents}
        cellSize={size}
        isLoading={isWaitingForSchedule}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

/** A payment that pays the document: what of it it pays, on both sides. */
type DocumentPaymentShare = Extract<
  NonNullable<DocumentPaymentsQuery["node"]>,
  { __typename?: "DocumentObject" }
>["paymentShares"][number];

/**
 * The bank payments that pay it, a row each, read for this tab alone: when
 * each was paid, the payment and the account it came from - each opened from
 * its chip - and what of the document it pays, with what they pay together
 * under them. Read, not changed here: a payment is matched to a document in
 * triage.
 */
function DocumentPaymentsTab({ size }: { size: "sm" | "md" }) {
  const store = useNodeLayoutStore();
  const { fieldDefinition, fieldValues } = useNodeField(store, "paymentShares");
  // A document being written has only its form's placeholder id: nothing
  // pays it yet.
  const documentId = store(
    useShallow((state) =>
      state.mode === "create"
        ? undefined
        : (state.fieldValues as DocumentFragment | undefined)?.id,
    ),
  );
  const { data, loading } = useBackendQuery(Queries.DOCUMENT_PAYMENTS_QUERY, {
    variables: { id: documentId! },
    skip: !documentId,
    fetchPolicy: "cache-and-network",
  });
  if (!fieldDefinition || fieldDefinition.type !== "table") {
    return null;
  }
  const document =
    data?.node?.__typename === "DocumentObject" ? data.node : null;
  const shares = document?.paymentShares ?? [];
  const valueOptions = fieldDefinition.getValueOptions?.(fieldValues) as {
    columns: TableColumnType[];
  };
  // What they pay of it together, under them, as a document's lines add up
  // under its lines - none where nothing pays it. The sum is drawn as its
  // column draws what each pays, its currency and all; every other cell here,
  // for those columns read a payment, which the row of the sum is not.
  const summaryRow =
    shares.length === 0
      ? undefined
      : Object.fromEntries(
          valueOptions.columns.map((column) => {
            if (column.objectKey === "effectiveDate") {
              return [
                column.objectKey,
                <Typography key={column.objectKey} level={`body-${size}`}>
                  Total
                </Typography>,
              ];
            }
            if (column.objectKey !== "amountDocument") {
              return [column.objectKey, <Fragment key={column.objectKey} />];
            }
            return [
              column.objectKey,
              shares
                .reduce(
                  (total, share) => total + Number(share.amountDocument ?? 0),
                  0,
                )
                .toFixed(2),
            ];
          }),
        );

  return (
    <div
      data-testid="document-payments"
      className="flex-1 min-h-0 w-full flex flex-col overflow-hidden"
    >
      <FieldTable
        value={shares as never}
        valueOptions={valueOptions}
        cellSize={size}
        isLoading={Boolean(documentId) && loading && !document}
        summaryRow={summaryRow}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

function DocumentPreviewTab() {
  const store = useNodeLayoutStore();
  // A document being written has only its form's placeholder id: no record
  // to read anything of it from.
  const documentId = store(
    useShallow((state) =>
      state.mode === "create"
        ? undefined
        : (state.fieldValues as DocumentFragment | undefined)?.id,
    ),
  );
  useHydrateDocumentLineItems(documentId);
  const document = store(
    useShallow((state) => state.fieldValues),
  ) as DocumentWithLineItems;
  // The node arrives after the store is seeded with its id, and which preview
  // to show depends on the document's type — so treat "no record yet" as
  // loading. A document being created has no record by definition.
  const isLoading = store(
    (state) =>
      state.mode !== "create" &&
      !(state.fieldValues as { identifier?: string })?.identifier,
  );
  // What an invoice's preview alone reads of it, as the server says it prints:
  // the look it prints with - none where the server says none, a template the
  // document named and lost among them - and whether the page it is shared at
  // takes a card, which only the server knows (its business's Stripe, what is
  // still owed). A skeleton while it is read, never first in a look it does
  // not have; anything but an invoice is its file.
  const {
    data: previewData,
    loading: isPreviewLoading,
    refetch: refetchPreview,
  } = useBackendQuery(Queries.DOCUMENT_PREVIEW_QUERY, {
    variables: { id: documentId! },
    skip: !documentId || document?.documentType !== DocumentEnum.Invoice,
    fetchPolicy: "cache-first",
    // Read again in place as it is saved, not waited on again.
    notifyOnNetworkStatusChange: false,
  });
  const preview =
    previewData?.node?.__typename === "DocumentObject"
      ? previewData.node
      : null;
  // A save moves ``updatedAt`` - a template picked, the invoice shared, a way
  // to pay added: the server has a newer answer than the cache.
  const documentUpdatedAt = document?.updatedAt;
  const lastUpdatedAtRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = lastUpdatedAtRef.current;
    lastUpdatedAtRef.current = documentUpdatedAt;
    if (!previewData || !previous || previous === documentUpdatedAt) {
      return;
    }
    void refetchPreview();
  }, [previewData, documentUpdatedAt, refetchPreview]);

  return (
    // Full height so a preview with nothing to show (the file drop target) can
    // centre itself in the panel rather than sitting against the top edge -
    // and the scroll of a file longer than that: a tab of the record's popper,
    // or a narrow panel, is a box that does not scroll, and left to the page
    // its first page filled it and the rest was cut off.
    <div className="flex flex-col w-full h-full flex-1 min-h-0 overflow-y-auto p-0">
      <DocumentPreview
        document={{
          ...(document as DocumentFragment),
          canAcceptPayments: preview?.canAcceptPayments,
        }}
        store={store}
        isLoading={isLoading || isPreviewLoading}
        template={preview?.template}
      />
    </div>
  );
}

const object: ObjectType<DocumentFragment> = {
  humanName: "document",
  inputKey: "document",
  typename: "DocumentObject",
  report: {
    source: "documents",
    description: "Invoices, bills and receipts, with what they came to",
    asks: "Which invoices are still unpaid, and who owes them?",
  },
  ReactIcon: iconsByTypename.DocumentObject,
  isEntity: false,
  titleFieldKey: "name",
  Card: DocumentCard,
  placeholder: {
    __typename: "DocumentObject",
    id: btoa("DocumentObject:placeholder"),
    identifier: "placeholder",
    name: "Invoice INV-1042",
    reference: "INV-1042",
    amountTotal: "1284.50",
    description: "Monthly bookkeeping and payroll services",
    documentType: DocumentEnum.Invoice,
    invoiceState: InvoiceStateEnum.Sent,
    dateDue: "2026-01-15",
    fileProcessingState: DocumentFileProcessingStateEnum.Processed,
    file: { name: "invoice.pdf", url: "#" },
    contact: {
      id: btoa("ContactObject:placeholder"),
      identifier: "placeholder",
      emailAddress: "billing@northwind.example",
      organization: {
        id: btoa("OrganizationObject:placeholder"),
        identifier: "placeholder",
        name: "Northwind Supply",
        logoUrl: null,
      },
    },
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as DocumentFragment,
  // What a document is called, wherever it is named - its card, its row,
  // its panel, a search's results, triage's cards: its name, the title it is
  // edited as and the list shows; where it has none, the number it prints,
  // else what it is about.
  getTitle: (data) =>
    data?.name?.trim() ||
    data?.reference?.trim() ||
    data?.description?.trim() ||
    undefined,
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  getReferenceValueOptions: (entityIdentifiers) => {
    return {
      query: gql`
        query DocumentList($entityIdentifiers: [String!]) {
          documents(entityIdentifiers: $entityIdentifiers) {
            data {
              id
              identifier
              name
              reference
              type
              invoiceState
              createdAt
              entity {
                id
                identifier
              }
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers: entityIdentifiers,
        },
      },
      filterOptions(options: DocumentFragment[], filter: string) {
        return options.filter((option) => {
          return (
            option?.name?.toLowerCase().includes(filter.toLowerCase()) ||
            option?.reference?.toLowerCase().includes(filter.toLowerCase()) ||
            option?.contact?.emailAddress
              ?.toLowerCase()
              .includes(filter.toLowerCase())
          );
        });
      },
      onRenderOption: (option: DocumentFragment) => {
        return option.name;
      },
      mutationOptions: {
        createTypename: "DocumentObject",
      },
    };
  },
  groups: [
    // Overview and Line Items share one column, tabbed, so the Preview keeps a
    // full-height column of its own beside them.
    {
      id: "overview",
      label: "Overview",
      isVisible: true,
      card: {
        stack: "details",
        stackMode: "tabs",
        flex: 1,
        minWidth: 320,
      },
    },
    {
      id: "lineItems",
      label: "Line Items",
      isVisible: true,
      component: DocumentLineItemsTab,
      flush: true,
      card: {
        stack: "details",
        stackMode: "tabs",
        isLabelVisible: true,
        flex: 1,
        isFlush: true,
      },
    },
    {
      id: "paymentSchedule",
      label: "Payment Schedule",
      // Only an invoice is paid in instalments — a bill, receipt or bank
      // statement has nothing to schedule.
      isVisible: (data) => data?.documentType === DocumentEnum.Invoice,
      component: DocumentPaymentScheduleTab,
      flush: true,
      card: {
        stack: "details",
        stackMode: "tabs",
        isLabelVisible: true,
        flex: 1,
        isFlush: true,
      },
    },
    {
      id: "payments",
      label: "Payments",
      // The bank's own record of its payments is matched line by line, not as
      // a document the payments settle.
      isVisible: (data) => data?.documentType !== DocumentEnum.BankStatement,
      component: DocumentPaymentsTab,
      flush: true,
      card: {
        stack: "details",
        stackMode: "tabs",
        isLabelVisible: true,
        flex: 1,
        isFlush: true,
      },
    },
    {
      id: "preview",
      label: "Preview",
      isVisible: true,
      component: DocumentPreviewTab,
      card: {
        flex: 1,
        minWidth: 600,
        isFlush: true,
      },
    },
  ],
  fieldDefinitions: [
    {
      objectKey: "budgetLines",
      label: "Budget",
      type: "custom",
      ReactIcon: WalletIcon,
      asString: () => "",
      getValue: (data) => () => <BudgetLinesOfRecord id={data?.id} />,
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: (data) =>
          Values.IS_BUDGET_SHOWN && data?.documentType === DocumentEnum.Bill,
      },
      summaryProps: { isVisible: false },
    },
    {
      objectKey: "id",
      linkedValuesKeys: ["id", "isPublic", "file"],
      label: "Preview",
      type: "custom",
      ReactIcon: CreditCardIcon,
      asString: (data) => data?.id,
      getValue: () => () => null,
      getValueOptions: () => ({
        placeholder: "Preview",
      }),
      groupId: "preview",
      createProps: {
        isRequired: false,
        isEditable: true,
        isFullWidth: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
        isFullWidth: true,
      },
    },
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "Entity",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.id,
      getValue: (data) => () => {
        return (
          <div className="w-full">
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Document",
      type: "reference",
      ReactIcon: iconsByTypename.DocumentObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "DocumentObject",
          logoUrl: data?.contact?.organization?.logoUrl,
          name: data?.name,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.entity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("DocumentObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "amountTotal",
      // Handed its currency, to say it where it is not its business's.
      linkedValuesKeys: ["amountTotal", "amountCurrency"],
      label: "Amount",
      type: "currency",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.amountTotal,
      getValue: (data) => {
        return data?.amountTotal;
      },
      // In another currency than its business's: its sign says which.
      getValueOptions: (data) => ({
        currency:
          data?.amountCurrency &&
          data?.entity?.currency &&
          data.amountCurrency !== data.entity.currency
            ? data.amountCurrency
            : null,
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // In another currency than its business's: the rate that turns it into
      // the business's - what its payments paid for it, else the official rate
      // the day it was issued, an estimate.
      objectKey: "amountToBusinessFx",
      // Handed what it is a rate from, and whose rate it is.
      linkedValuesKeys: [
        "amountToBusinessFx",
        "amountCurrency",
        "isAmountToBusinessFxOfficial",
      ],
      label: "Exchange rate",
      type: "text",
      ReactIcon: ArrowRightLeftIcon,
      asString: (data) =>
        data?.amountToBusinessFx != null
          ? `1 ${data.amountCurrency} = ${Number(data.amountToBusinessFx).toFixed(4)} ${data.entity?.currency ?? ""}${data.isAmountToBusinessFxOfficial ? " · official rate, an estimate" : ""}`
          : "",
      getValue: (data) =>
        data?.amountToBusinessFx != null
          ? `1 ${data.amountCurrency} = ${Number(data.amountToBusinessFx).toFixed(4)} ${data.entity?.currency ?? ""}${data.isAmountToBusinessFxOfficial ? " · official rate, an estimate" : ""}`
          : "No rate known",
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: (data) =>
          Boolean(
            data?.amountCurrency &&
            data?.entity?.currency &&
            data.amountCurrency !== data.entity.currency,
          ),
      },
      summaryProps: {
        isVisible: (data) =>
          Boolean(
            data?.amountCurrency &&
            data?.entity?.currency &&
            data.amountCurrency !== data.entity.currency,
          ),
      },
    },
    {
      // What it comes to in its business's currency, at that rate.
      objectKey: "amountTotalBusiness",
      // Handed the currency it was converted from.
      linkedValuesKeys: ["amountTotalBusiness", "amountCurrency"],
      label: "Converted amount",
      type: "currency",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.amountTotalBusiness,
      getValue: (data) => data?.amountTotalBusiness,
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: (data) =>
          Boolean(
            data?.amountCurrency &&
            data?.entity?.currency &&
            data.amountCurrency !== data.entity.currency,
          ),
      },
      summaryProps: {
        isVisible: (data) =>
          Boolean(
            data?.amountCurrency &&
            data?.entity?.currency &&
            data.amountCurrency !== data.entity.currency,
          ),
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name,
      getValue: (data) => {
        return data?.name;
      },
      getValueOptions: () => ({
        placeholder: "Invoice 123, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.description,
      getValue: (data) => {
        return data?.description;
      },
      getValueOptions: () => ({
        placeholder:
          "Invoice for 100 hours of project management services for the month of January 2025, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "reference",
      label: "Reference",
      type: "text",
      ReactIcon: HashIcon,
      asString: (data) => data?.reference,
      getValue: (data) => {
        return data?.reference;
      },
      getValueOptions: () => ({
        placeholder: "INV-123, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // The day it is dated, as it prints: an invoice made here, the day it
      // was made, until another is picked.
      objectKey: "dateIssued",
      label: "Issue date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.dateIssued ?? "",
      getValue: (data) => data?.dateIssued,
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
    {
      // The day it is due by, as it prints: none, due on receipt.
      objectKey: "dateDue",
      label: "Due date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.dateDue ?? "",
      getValue: (data) => data?.dateDue,
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
    {
      objectKey: "contact",
      inputKey: "contact",
      label: "Contact",
      type: "reference",
      ReactIcon: UserIcon,
      asString: (data) => data?.contact?.emailAddress,
      getValue: (data) => ({
        __typename: "ContactObject",
        name: data?.contact?.emailAddress,
        id: data?.contact?.id,
        identifier: data?.contact?.identifier,
        entityId: data?.entity?.id,
      }),
      getValueOptions: (data) =>
        getReferenceValueOptionsByTypename("ContactObject")([
          data?.entity?.identifier,
        ]),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "reference",
        //     isSortable: true,
        isFilterable: true,
      },
    },
    {
      // The bank account a statement is of: its import writes it, and it is
      // set by hand where the import could not match one. Only a statement
      // has one - the server lets go of it when the type changes.
      objectKey: "financialAccount",
      inputKey: "financialAccount",
      label: "Bank account",
      type: "reference",
      ReactIcon: LandmarkIcon,
      asString: (data) => data?.financialAccount?.name ?? "",
      getValue: (data) => ({
        __typename: "FinancialAccountObject",
        name: data?.financialAccount?.name ?? null,
        id: data?.financialAccount?.id ?? null,
        identifier: data?.financialAccount?.identifier ?? null,
      }),
      getValueOptions: (data) => ({
        isNullable: true,
        ...getReferenceValueOptionsByTypename("FinancialAccountObject")([
          data?.entity?.identifier,
        ]),
      }),
      updateProps: {
        isRequired: false,
        isEditable: (data) => data?.documentType === DocumentEnum.BankStatement,
        isVisible: (data) =>
          data?.documentType === DocumentEnum.BankStatement ||
          Boolean(data?.financialAccount),
      },
    },
    {
      // How an invoice looks and what it tells its payer: its business's
      // default as it was made, or another of its templates. Never none once
      // the business has one, so it is changed, not cleared.
      objectKey: "template",
      inputKey: "template",
      label: "Template",
      type: "reference",
      ReactIcon: iconsByTypename.DocumentTemplateObject,
      asString: (data) => data?.template?.name ?? "",
      getValue: (data) => ({
        __typename: "DocumentTemplateObject",
        name: data?.template?.name ?? null,
        id: data?.template?.id ?? null,
        identifier: data?.template?.identifier ?? null,
      }),
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("DocumentTemplateObject")([
          data?.entity?.identifier,
        ]),
      }),
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => data?.documentType === DocumentEnum.Invoice,
      },
    },
    {
      // Who emailed it in: shown for a document that arrived by email.
      objectKey: "fromEmailAddress",
      label: "From email",
      type: "string",
      ReactIcon: MailIcon,
      asString: (data) => data?.fromEmailAddress ?? "",
      getValue: (data) => data?.fromEmailAddress,
      updateProps: {
        isEditable: false,
        isVisible: (data) => Boolean(data?.fromEmailAddress),
      },
    },
    {
      // Linked either way round: a counterpart is this document in another
      // entity's books, which opening it shows.
      objectKey: "outgoingLinks",
      label: "Linked documents",
      type: "references",
      ReactIcon: LinkIcon,
      asString: (data) =>
        [
          ...(data?.outgoingLinks ?? []).map((link) => link.target),
          ...(data?.incomingLinks ?? []).map((link) => link.source),
        ]
          .map((document) => document?.name)
          .join(", "),
      getValue: (data) =>
        [
          ...(data?.outgoingLinks ?? []).map((link) => link.target),
          ...(data?.incomingLinks ?? []).map((link) => link.source),
        ].map((document) => ({
          __typename: "DocumentObject",
          id: document?.id,
          identifier: document?.identifier,
          name: document?.name ?? "Document",
        })),
      updateProps: {
        isEditable: false,
        isVisible: (data) =>
          (data?.outgoingLinks?.length ?? 0) +
            (data?.incomingLinks?.length ?? 0) >
          0,
      },
    },
    {
      objectKey: "createdAt",
      label: "Date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.createdAt,
      getValue: (data) => {
        return data?.createdAt;
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "documentType",
      inputKey: "type",
      label: "Type",
      type: "enum",
      ReactIcon: GitBranchIcon,
      asString: (data) => data?.documentType,
      getValue: (data) => data?.documentType,
      getValueOptions: () => ({
        enum: _.map(DocumentEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [DocumentEnum.Invoice]: EnumColors.BLUE[mode],
          [DocumentEnum.PurchaseOrder]: EnumColors.AQUA[mode],
        }),
      }),
      // A file is read as whatever it is headed, and a supplier's "Invoice"
      // is a bill in the books it came to: the type is put right by hand.
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      createProps: {
        isRequired: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        exportPath: "type",
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "amountCurrency",
      inputKey: "amountCurrency",
      label: "Currency",
      type: "enum",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.amountCurrency ?? "",
      getValue: (data) => data?.amountCurrency,
      getValueOptions: () => ({
        enum: _.map(CurrencyEnum, (value) => ({ key: value, value })),
        // Said as its code - USD, not "Usd".
        keepsCase: true,
      }),
      // A bill printing '$' alone is read as whichever dollar the reader
      // guesses: put right here, it is matched and booked in the right one.
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "invoiceState",
      inputKey: "invoiceState",
      label: "State",
      type: "enum",
      ReactIcon: GitBranchIcon,
      asString: () => "Unknown",
      getValue: (data) => data?.invoiceState,
      getValueOptions: () => ({
        enum: _.map(InvoiceStateEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [InvoiceStateEnum.Paid]: EnumColors.GREEN[mode],
          [InvoiceStateEnum.Draft]: EnumColors.GREY_1[mode],
          [InvoiceStateEnum.Sent]: EnumColors.BLUE[mode],
        }),
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
    },
    {
      objectKey: "isPublic",
      label: "Is public",
      type: "boolean",
      ReactIcon: LinkIcon,
      asString: (data) => (data?.isPublic ? "Yes" : "No"),
      getValue: (data) => data?.isPublic,
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "url",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "paymentMethods",
      label: "Payment methods",
      type: "enums",
      ReactIcon: CreditCardIcon,
      asString: (data) =>
        _.map(data?.paymentMethods, (value) => value?.toString()).join(", "),
      getValue: (data) => data?.paymentMethods || [],
      getValueOptions: () => ({
        enum: _.map(DocumentPaymentMethodEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [DocumentPaymentMethodEnum.CreditCard]: EnumColors.GREEN[mode],
          [DocumentPaymentMethodEnum.ETransfer]: EnumColors.ORANGE[mode],
        }),
      }),
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "url",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "link",
      label: "Link",
      type: "url",
      ReactIcon: LinkIcon,
      asString: (data) =>
        "/public/" +
        identifierAndTypenameToId(data?.identifier, "PublicDocumentObject"),
      getValue: (data) => {
        if (!data?.isPublic) {
          return {
            url: "",
            label: "",
          };
        }
        return {
          url:
            "/public/" +
            identifierAndTypenameToId(data?.identifier, "PublicDocumentObject"),
          label: "Open in new tab",
        };
      },
      linkedValuesKeys: ["identifier", "isPublic"],
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "url",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "pictures",
      label: "Pictures",
      type: "pictures",
      ReactIcon: ImageIcon,
      asString: (data) =>
        (data?.pictures || []).map((p) => p.name || p.identifier).join(", "),
      getValue: (data) => data?.pictures || [],
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: false,
      },
    },
    {
      objectKey: "lineItems",
      label: "Line Items",
      type: "table",
      ReactIcon: TableIcon,
      groupId: "lineItems",
      // The type decides which columns a line has (see the columns below).
      linkedValuesKeys: [
        "lineItems",
        "entity",
        "documentType",
        "amountCurrency",
        "amountToBusinessFx",
      ],
      asString: () => "",
      getValue: (data) =>
        ((data as DocumentWithLineItems)?.lineItems || []).map((lineItem) => {
          if (lineItem.metadata?._frontend_uuid) {
            return lineItem;
          }
          // Prefer identifier as the stable client key — never mint a fresh
          // random uuid for a persisted row (that reminting caused duplicates).
          if (lineItem.identifier) {
            return {
              ...lineItem,
              metadata: {
                ...(lineItem.metadata ?? {}),
                _frontend_uuid: lineItem.identifier,
              },
            };
          }
          return {
            ...lineItem,
            metadata: {
              ...(lineItem.metadata ?? {}),
              _frontend_uuid: crypto.randomUUID(),
            },
          };
        }),
      getValueOptions: (data) => {
        // A document in another currency than its business's: its amounts say
        // which, and what they come to in the business's is beside them.
        const inAnotherCurrency = Boolean(
          data?.amountCurrency &&
          data?.entity?.currency &&
          data.amountCurrency !== data.entity.currency,
        );
        // The tax on a line, derived from the line's own tax rather than read
        // back from the server, so picking a tax or editing the amount updates
        // it straight away instead of after a save round trip.
        //
        // The arithmetic mirrors ``TaxesService.apply_line_tax``: each rate is
        // applied to the line subtotal and rounded to cents on its own before
        // being summed, which a compound tax rounded once at the end can
        // differ from by a cent.
        const computeTax = (lineItemTransaction: DocumentLineItem) => {
          const tax = lineItemTransaction?.tax;
          if (!tax?.identifier) {
            return 0;
          }
          // What the document prints, where that is not what its rates make -
          // a cent apart, as read - is the line's own, kept by the server.
          if (
            (lineItemTransaction?.metadata as Record<string, unknown> | null)
              ?._tax_manual_override &&
            lineItemTransaction.amountTax != null
          ) {
            return Number(lineItemTransaction.amountTax);
          }
          const subtotal = Number(
            lineItemTransaction?.amountSubtotal ??
              lineItemTransaction?.amount ??
              0,
          );
          if (!subtotal) {
            return 0;
          }
          const rates = (tax.rates ?? []).filter(Boolean);
          if (rates.length > 0) {
            return rates.reduce(
              (total, rate) =>
                total + Math.round(subtotal * Number(rate?.rate ?? 0)) / 100,
              0,
            );
          }
          // A tax reference picked before its rates were loaded still carries
          // the total, and a stored amount is the last resort.
          if (tax.totalRate != null) {
            return Math.round(subtotal * Number(tax.totalRate)) / 100;
          }
          return Number(lineItemTransaction?.amountTax ?? 0);
        };
        // What one unit of a line costs: its amount over its quantity, as the
        // invoice prints it - or, while its quantity is being typed, the price
        // the quantity was typed against, kept on the row so that clearing
        // "3" on the way to "2" does not price two at all three's amount.
        const unitPriceOf = (lineItemTransaction: DocumentLineItem) => {
          if (lineItemTransaction?.unitPrice != null) {
            return Number(lineItemTransaction.unitPrice);
          }
          const subtotal = Number(
            lineItemTransaction?.amountSubtotal ??
              lineItemTransaction?.amount ??
              0,
          );
          const quantity = Number(lineItemTransaction?.quantity ?? 1);
          return quantity > 0 ? subtotal / quantity : subtotal;
        };
        return {
          columns: [
            {
              label: "Date",
              type: "date",
              width: 200,
              minWidth: 200,
              headerFieldType: "date",
              objectKey: "effectiveDate",
              // When the bank posted it: read off the statement, not typed.
              isDisabled: true,
              isSearchable: false,
              getValue: (lineItemTransaction: DocumentLineItem) =>
                lineItemTransaction?.effectiveDate,
              getValueForApi: () => undefined,
            },
            {
              label: "Description",
              type: "string",
              // The least it is: it takes what the others leave beyond that.
              width: 400,
              minWidth: 400,
              headerFieldType: "string",
              objectKey: "description",
              isDisabled: false,
              isSearchable: false,
              isMultiline: true,
              getValue: (lineItemTransaction: DocumentLineItem) =>
                lineItemTransaction?.description || "",
              getValueOptions: () => ({
                placeholder:
                  "Project overview & design, stakeholder meetings, etc.",
              }),
            },
            {
              // The account each line books to - what booking the document
              // posts it to, to put right before then.
              label: "Account",
              type: "reference",
              width: 200,
              minWidth: 200,
              headerFieldType: "string",
              objectKey: "chartedAccount",
              inputKey: "chartedAccount",
              isDisabled: false,
              isSearchable: false,
              getValue: (lineItemTransaction: DocumentLineItem) => ({
                __typename: "ChartedAccountObject",
                id: lineItemTransaction?.chartedAccount?.id,
                identifier: lineItemTransaction?.chartedAccount?.identifier,
                name: lineItemTransaction?.chartedAccount?.name,
              }),
              getValueForApi: (lineItemTransaction: DocumentLineItem) =>
                lineItemTransaction?.chartedAccount?.identifier
                  ? {
                      identifier: lineItemTransaction.chartedAccount.identifier,
                    }
                  : undefined,
              getValueOptions: () => ({
                isNullable: true,
                ...getReferenceValueOptionsByTypename("ChartedAccountObject")([
                  data?.entity?.identifier,
                ]),
              }),
            },
            {
              label: "Quantity",
              type: "number",
              width: 200,
              minWidth: 200,
              headerFieldType: "number",
              objectKey: "quantity",
              isDisabled: false,
              isSearchable: false,
              // A line with no quantity is one unit — which is what the
              // preview and the invoice canvas have always displayed. Reading
              // it as 0 instead meant any save rewrote an unset quantity to 0,
              // zeroing the line's unit price on rows the user never touched.
              getValue: (lineItemTransaction: DocumentLineItem) =>
                lineItemTransaction?.quantity ?? 1,
              getValueForApi: (lineItemTransaction: DocumentLineItem) =>
                lineItemTransaction?.quantity == null ||
                (lineItemTransaction.quantity as unknown) === ""
                  ? 1
                  : Number(lineItemTransaction.quantity),
              getValueOptions: () => ({
                placeholder: "1",
              }),
              // The quantity scales the line at its unit price: three hours at
              // $150 come to $450. A quantity cleared on the way to another
              // leaves the amount as it was.
              preprocessOnChange: (_key, value, row) => {
                const unitPrice = unitPriceOf(row);
                const quantity = Number(value) || 0;
                if (quantity <= 0) {
                  return { ...row, unitPrice };
                }
                const amount = Math.round(unitPrice * quantity * 100) / 100;
                return { ...row, unitPrice, amount, amountSubtotal: amount };
              },
            },
            {
              // Never sent: the line's amount is what the API keeps, and the
              // unit price is read back from it and its quantity.
              label: "Unit price",
              type: "currency",
              width: 200,
              minWidth: 200,
              headerFieldType: "number",
              objectKey: "unitPrice",
              isDisabled: false,
              isSearchable: false,
              getValue: unitPriceOf,
              getValueOptions: () => ({
                placeholder: "$150.00",
                currency: inAnotherCurrency ? data?.amountCurrency : null,
              }),
              preprocessOnChange: (_key, value, row) => {
                const quantity = Number(row?.quantity ?? 1);
                const amount =
                  Math.round(
                    (Number(value) || 0) * (quantity > 0 ? quantity : 1) * 100,
                  ) / 100;
                return { ...row, amount, amountSubtotal: amount };
              },
              getValueForApi: () => undefined,
            },
            {
              label: "Amount",
              type: "currency",
              // The least it is drawn at, as every column but the description
              // is: a figure and its sign, an account's name, never cut short.
              width: 200,
              minWidth: 200,
              headerFieldType: "number",
              objectKey: "amount",
              isDisabled: false,
              isSearchable: false,
              getValue: (lineItemTransaction: DocumentLineItem) =>
                lineItemTransaction?.amountSubtotal ??
                lineItemTransaction?.amount ??
                0,
              getValueOptions: () => ({
                placeholder: "$100.00",
                currency: inAnotherCurrency ? data?.amountCurrency : null,
              }),
              // The grid writes a cell to its ``objectKey`` alone, and this
              // column reads ``amountSubtotal`` first, which every saved line
              // has: typed into ``amount`` only, the edit was read past - the
              // old subtotal shown and sent, and saved back over it. Both
              // keys move together, as the invoice canvas writes them. A unit
              // price kept from typing a quantity gives way to the amount
              // typed, which it is read from again.
              preprocessOnChange: (_key, value, row) => ({
                ...row,
                amountSubtotal: value,
                unitPrice: undefined,
              }),
              getValueForApi(lineItemTransaction: DocumentLineItem) {
                // A row added from the grid seeds its cells with "", which the
                // API rejects outright ("cannot represent a Decimal"), failing
                // the whole save. An empty amount is zero.
                const value = (lineItemTransaction?.amountSubtotal ??
                  lineItemTransaction?.amount) as unknown;
                return value == null || value === "" ? 0 : Number(value);
              },
            },
            {
              label: "Tax",
              type: "reference",
              width: 200,
              minWidth: 200,
              headerFieldType: "string",
              objectKey: "tax",
              inputKey: "tax",
              isDisabled: false,
              isSearchable: false,
              // Sits with the numbers it applies to rather than against the
              // description column.
              style: { textAlign: "right" },
              getValue: (lineItemTransaction: DocumentLineItem) => ({
                __typename: "TaxObject",
                name: lineItemTransaction?.tax?.name,
                id: lineItemTransaction?.tax?.id,
                identifier: lineItemTransaction?.tax?.identifier,
              }),
              getValueForApi: (lineItemTransaction: DocumentLineItem) => ({
                identifier: lineItemTransaction?.tax?.identifier,
              }),
              getValueOptions: () => {
                const taxOptions = getReferenceValueOptionsByTypename(
                  "TaxObject",
                )([data?.entity?.identifier]);
                return {
                  isNullable: true,
                  ...taxOptions,
                  mutationOptions: {
                    ...taxOptions?.mutationOptions,
                    createNewLabel: "Create new tax",
                    createTypename: "TaxObject",
                  },
                };
              },
            },
            {
              label: "Tax amount",
              type: "currency",
              width: 200,
              minWidth: 200,
              headerFieldType: "number",
              objectKey: "amountTax",
              isDisabled: true,
              isSearchable: false,
              getValue: computeTax,
              getValueOptions: () => ({
                placeholder: "$13.00",
                currency: inAnotherCurrency ? data?.amountCurrency : null,
              }),
              // Display only: the API derives a line's tax from ``tax`` and
              // ``amount`` and ``UpdateDocumentLineItemInputObject`` has no
              // ``amountTax`` field, so sending one fails the whole mutation.
              // (The manual-override flag this column used to stamp into
              // ``metadata._tax_manual_override`` never reached the API either
              // — the submit strips ``_``-prefixed keys.)
              getValueForApi: () => undefined,
            },
            {
              // What the line comes to: its amount and the tax on it. Read,
              // never sent - the API works it out from the two.
              label: "Total",
              type: "currency",
              width: 200,
              minWidth: 200,
              headerFieldType: "number",
              objectKey: "total",
              isDisabled: true,
              isSearchable: false,
              // In bold down: what each line comes to.
              style: { fontWeight: "600" },
              getValue: (lineItemTransaction: DocumentLineItem) =>
                Number(
                  lineItemTransaction?.amountSubtotal ??
                    lineItemTransaction?.amount ??
                    0,
                ) + computeTax(lineItemTransaction),
              getValueOptions: () => ({
                currency: inAnotherCurrency ? data?.amountCurrency : null,
              }),
              getValueForApi: () => undefined,
            },
            {
              // In another currency than its business's: what the line comes
              // to in the business's, at the document's rate. Read, never sent.
              label: `Total (${data?.entity?.currency ?? ""})`,
              type: "currency",
              width: 200,
              minWidth: 200,
              headerFieldType: "number",
              objectKey: "totalBusiness",
              isDisabled: true,
              isSearchable: false,
              getValue: (lineItemTransaction: DocumentLineItem) =>
                (Number(
                  lineItemTransaction?.amountSubtotal ??
                    lineItemTransaction?.amount ??
                    0,
                ) +
                  computeTax(lineItemTransaction)) *
                Number(data?.amountToBusinessFx ?? 0),
              getValueForApi: () => undefined,
            },
          ].filter((column) =>
            // A statement line is when it posted, what it says and what it
            // came to; anything else's is how many of what at what price, with tax - and
            // what that comes to, where a line is taxed - and the account each
            // line books to. An invoice the business sends books to its
            // revenue account alone, as a rule: said only where its lines book
            // to different ones.
            data?.documentType === DocumentEnum.BankStatement
              ? ["effectiveDate", "description", "amount"].includes(
                  column.objectKey,
                )
              : column.objectKey === "chartedAccount"
                ? data?.documentType !== DocumentEnum.Invoice ||
                  new Set(
                    ((data as DocumentWithLineItems)?.lineItems ?? []).map(
                      (lineItem) => lineItem.chartedAccount?.identifier,
                    ),
                  ).size > 1
                : // What a line comes to is its amount alone where none
                  // is taxed: said only where one is.
                  column.objectKey === "total"
                  ? ((data as DocumentWithLineItems)?.lineItems ?? []).some(
                      (lineItem) => lineItem.tax?.identifier,
                    )
                  : // Converted only where it is in another currency, at a
                    // rate known.
                    column.objectKey === "totalBusiness"
                    ? inAnotherCurrency && data?.amountToBusinessFx != null
                    : column.objectKey !== "effectiveDate",
          ),
        } as { columns: TableColumnType[] };
      },
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
    {
      objectKey: "paymentSchedule",
      inputKey: "paymentSchedule",
      label: "Payment Schedule",
      type: "table",
      ReactIcon: CalendarDaysIcon,
      groupId: "paymentSchedule",
      linkedValuesKeys: ["paymentSchedule", "amountTotal", "entity"],
      asString: () => "",
      getValue: (data) =>
        ((data as DocumentWithPaymentSchedule)?.paymentSchedule || []).map(
          (item) => {
            if (item.metadata?._frontend_uuid) {
              return item;
            }
            return {
              ...item,
              metadata: {
                ...(item.metadata ?? {}),
                _frontend_uuid: item.identifier ?? crypto.randomUUID(),
              },
            };
          },
        ),
      getValueOptions: (data) =>
        ({
          columns: [
            {
              label: "Instalment",
              type: "string",
              width: 160,
              headerFieldType: "string",
              objectKey: "name",
              isDisabled: false,
              isSearchable: false,
              getValue: (item: DocumentPaymentScheduleItem) => item?.name || "",
              getValueOptions: () => ({
                placeholder: "Deposit, On completion, ...",
              }),
            },
            {
              label: "%",
              type: "number",
              width: 70,
              headerFieldType: "number",
              objectKey: "percent",
              isDisabled: false,
              isSearchable: false,
              style: { textAlign: "right" },
              getValue: (item: DocumentPaymentScheduleItem) =>
                item?.percent ?? null,
              // An instalment is either a share of the invoice or a fixed sum.
              // Blanking the percent must send null, not 0, or the row would
              // silently become a 0% instalment worth nothing.
              getValueForApi: (item: DocumentPaymentScheduleItem) => {
                const value = item?.percent;
                return value == null || value === "" ? null : Number(value);
              },
              getValueOptions: () => ({
                placeholder: "50",
              }),
            },
            {
              label: "Amount",
              type: "currency",
              width: 110,
              headerFieldType: "number",
              objectKey: "amount",
              isDisabled: false,
              isSearchable: false,
              style: { textAlign: "right" },
              // A percentage instalment shows what it currently works out to,
              // recomputed here rather than read back, so editing a line item
              // or the percentage updates the cell without a save round trip.
              // The arithmetic matches ``DocumentsService.sync_payment_schedule``.
              getValue: (item: DocumentPaymentScheduleItem) => {
                if (item?.percent == null || item.percent === "") {
                  return item?.amount ?? 0;
                }
                const total = Number(
                  (data as DocumentWithPaymentSchedule)?.amountTotal ?? 0,
                );
                return Math.round(total * Number(item.percent)) / 100;
              },
              getValueForApi: (item: DocumentPaymentScheduleItem) => {
                const value = item?.amount;
                return value == null || value === "" ? 0 : Number(value);
              },
              getValueOptions: () => ({
                placeholder: "$500.00",
              }),
            },
            {
              label: "Due",
              type: "date",
              width: 130,
              headerFieldType: "string",
              objectKey: "dateDue",
              isDisabled: false,
              isSearchable: false,
              getValue: (item: DocumentPaymentScheduleItem) =>
                item?.dateDue ?? null,
              getValueOptions: () => ({
                placeholder: "On completion",
              }),
            },
            {
              label: "Status",
              type: "string",
              width: 150,
              headerFieldType: "string",
              objectKey: "status",
              isDisabled: true,
              isSearchable: false,
              getValue: (item: DocumentPaymentScheduleItem) =>
                PAYMENT_SCHEDULE_STATUS_LABELS[
                  item?.status ?? DocumentPaymentScheduleStatusEnum.Pending
                ],
              // Display-only: the status moves through payment and
              // acknowledgement, never through this grid.
              getValueForApi: () => undefined,
              getValueOptions: () => ({ placeholder: "" }),
            },
          ],
        }) as { columns: TableColumnType[] },
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
    {
      // The payments that pay it, read by its Payments tab
      // (``DocumentPaymentsTab``), not with the document: a table to read,
      // nothing in it changed here.
      objectKey: "paymentShares",
      label: "Payments",
      type: "table",
      ReactIcon: CreditCardIcon,
      groupId: "payments",
      linkedValuesKeys: ["entity", "amountCurrency"],
      asString: () => "",
      getValue: () => [],
      getValueOptions: (data) => {
        // A document in another currency than its business's: what each pays
        // of it says which.
        const inAnotherCurrency = Boolean(
          data?.amountCurrency &&
          data?.entity?.currency &&
          data.amountCurrency !== data.entity.currency,
        );
        return {
          columns: [
            {
              label: "Date",
              type: "date",
              width: 120,
              headerFieldType: "string",
              objectKey: "effectiveDate",
              isDisabled: true,
              isSearchable: false,
              getValue: (share: DocumentPaymentShare) =>
                share.transaction.effectiveDate,
              getValueOptions: () => ({}),
            },
            {
              label: "Payment",
              type: "reference",
              width: 260,
              headerFieldType: "string",
              objectKey: "transaction",
              isDisabled: true,
              isSearchable: false,
              getValue: (share: DocumentPaymentShare) => ({
                __typename: "TransactionObject",
                id: share.transaction.id,
                identifier: share.transaction.identifier,
                name: share.transaction.description,
              }),
              getValueOptions: () =>
                getReferenceValueOptionsByTypename("TransactionObject")([
                  data?.entity?.identifier,
                ]),
            },
            {
              label: "Account",
              type: "reference",
              width: 200,
              headerFieldType: "string",
              objectKey: "financialAccount",
              isDisabled: true,
              isSearchable: false,
              getValue: (share: DocumentPaymentShare) => ({
                __typename: "FinancialAccountObject",
                id: share.transaction.financialAccount?.id ?? null,
                identifier:
                  share.transaction.financialAccount?.identifier ?? null,
                name: share.transaction.financialAccount?.name ?? null,
              }),
              getValueOptions: () =>
                getReferenceValueOptionsByTypename("FinancialAccountObject")([
                  data?.entity?.identifier,
                ]),
            },
            {
              // What of the document it pays, in the document's currency.
              label: "Paid",
              type: "currency",
              width: 120,
              headerFieldType: "number",
              objectKey: "amountDocument",
              isDisabled: true,
              isSearchable: false,
              style: { textAlign: "right" },
              getValue: (share: DocumentPaymentShare) => share.amountDocument,
              getValueOptions: () => ({
                currency: inAnotherCurrency ? data?.amountCurrency : null,
              }),
            },
          ],
        } as { columns: TableColumnType[] };
      },
      createProps: {
        isRequired: false,
        isEditable: false,
        isVisible: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
    },
  ],
  getActions: (data) => {
    return [
      // Filed in the wrong business - imported a moment ago into the wrong
      // one, say - it is moved, not made again: its lines, contact and tags
      // go with it. A draft is moved from the chat it waits in, which files
      // it afresh.
      ...((data as { pendingChange?: unknown } | undefined)?.pendingChange
        ? []
        : State.getState()
            .entities.filter(
              (entity) =>
                entity.identifier &&
                entity.identifier !== data?.entity?.identifier,
            )
            .map((entity) => ({
              label: `Move to ${entity.name}`,
              icon: BuildingIcon,
              isIconButton: false,
              mutationOptions: {
                mutation: Mutations.MOVE_NODE_TO_ENTITY,
                options: {
                  variables: {
                    input: {
                      id: data?.id,
                      entityIdentifier: entity.identifier,
                    },
                  },
                },
              },
            }))),
      // Its file read again - a mistake in what it says, or in the currency
      // it was filed in, put right. A draft is read again in its chat, and an
      // email's own body is no file to read; one with something booked
      // against it is refused in the command, which asks the server.
      ...(data?.file &&
      data.fileProcessingState !==
        DocumentFileProcessingStateEnum.NotApplicable &&
      !(data as { pendingChange?: unknown } | undefined)?.pendingChange
        ? [
            {
              label: "Read again",
              icon: RotateCwIcon,
              isIconButton: false,
              onClick: () =>
                State.getState().pushToCommandStack({
                  commandDefinitionId: "read-document-again",
                  context: {
                    id: data.id,
                    identifier: data.identifier,
                    name: data.name,
                    currency: data.amountCurrency,
                  },
                }),
            },
          ]
        : []),
      // Its file saved under its own name. Storage is another origin, where a
      // link's download name is ignored and the file only opens, so it is
      // fetched and saved from here - and opened, should that fail.
      ...(data?.file?.url
        ? [
            {
              label: "Download",
              icon: DownloadIcon,
              isIconButton: false,
              onClick: () => {
                const { url, name } = data.file!;
                void fetch(url!)
                  .then((response) =>
                    response.ok
                      ? response.blob()
                      : Promise.reject(new Error(String(response.status))),
                  )
                  .then((blob) => {
                    const saved = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = saved;
                    link.download = name || "document";
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(saved);
                  })
                  .catch(() => window.open(url!, "_blank", "noopener"));
              },
            },
          ]
        : []),
      // An invoice the business sends, emailed to its contact; a bill or a
      // receipt is from whoever it is from, and is not sent anywhere.
      ...(data?.documentType === DocumentEnum.Invoice
        ? [
            {
              label: "Send by email",
              icon: SendIcon,
              isIconButton: false,
              onClick: () =>
                State.getState().pushToCommandStack({
                  commandDefinitionId: "send-document",
                  context: {
                    identifier: data.identifier,
                    title: data.reference || data.name,
                    contactIdentifier: data.contact?.identifier,
                    emailAddress: data.contact?.emailAddress,
                    organizationName: data.contact?.organization?.name,
                  },
                }),
            },
          ]
        : []),
    ];
  },
};

export { object as DocumentObject };
